import React, { createContext, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

/**
 * Multi-Country admin dashboard scoping.
 *
 * URL pattern:  /admin              → All Countries (super view)
 *               /admin/{slug}       → Scope to one country (e.g. /admin/brunei)
 *
 * Any list query in the CMS should pass `country_slug` when present
 * so backend endpoints filter accordingly. The country switcher in the
 * admin header updates the URL, which cascades through this context.
 */
const CountryContext = createContext({ activeSlug: null, activeCountry: null, countries: [], setSlug: () => {} });

export const CountryScopeProvider = ({ children }) => {
  const { countrySlug } = useParams();
  const navigate = useNavigate();
  const { data: countries = [] } = useQuery({
    queryKey: ["admin-countries"],
    queryFn: async () => (await api.get("/countries")).data,
    staleTime: 60_000,
  });
  const activeCountry = useMemo(
    () => (countrySlug ? countries.find((c) => c.slug === countrySlug) : null),
    [countrySlug, countries]
  );
  const setSlug = (slug) => navigate(slug ? `/admin/${slug}` : "/admin");
  return (
    <CountryContext.Provider value={{ activeSlug: countrySlug || null, activeCountry, countries, setSlug }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountryScope = () => useContext(CountryContext);
